import { useCallback, useRef } from 'react';

let sharedCtx: AudioContext | null = null;

const getCtx = (): AudioContext => {
  if (!sharedCtx || sharedCtx.state === 'closed') {
    sharedCtx = new AudioContext();
  }
  if (sharedCtx.state === 'suspended') {
    sharedCtx.resume();
  }
  return sharedCtx;
};

const playTone = (
  freq: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume = 0.08,
  freqEnd?: number,
) => {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    if (freqEnd) osc.frequency.linearRampToValueAtTime(freqEnd, ctx.currentTime + duration);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch { /* audio not available */ }
};

const playNote = (freq: number, startTime: number, duration: number, volume = 0.06) => {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startTime);
    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration);
  } catch { /* audio not available */ }
};

export const useSound = () => {
  const volumeRef = useRef(1.0);
  const enabledRef = useRef(true);

  const isEnabled = () => enabledRef.current && volumeRef.current > 0;

  const playMove = useCallback(() => {
    if (!isEnabled()) return;
    playTone(220, 0.06, 'triangle', 0.05 * volumeRef.current, 280);
  }, []);

  const playMerge = useCallback((tileValue = 4) => {
    if (!isEnabled()) return;
    const basePitch = Math.min(300 + Math.log2(tileValue) * 80, 1200);
    playTone(basePitch, 0.12, 'sine', 0.07 * volumeRef.current, basePitch * 1.2);
  }, []);

  const playSpawn = useCallback(() => {
    if (!isEnabled()) return;
    playTone(600, 0.04, 'sine', 0.03 * volumeRef.current, 800);
  }, []);

  const playTick = useCallback(() => {
    if (!isEnabled()) return;
    playTone(880, 0.06, 'square', 0.04 * volumeRef.current);
  }, []);

  const playCountdown = useCallback(() => {
    if (!isEnabled()) return;
    playTone(523, 0.15, 'sine', 0.08 * volumeRef.current);
  }, []);

  const playGo = useCallback(() => {
    if (!isEnabled()) return;
    playTone(1047, 0.3, 'sine', 0.1 * volumeRef.current);
  }, []);

  const playVictory = useCallback(() => {
    if (!isEnabled()) return;
    try {
      const ctx = getCtx();
      const now = ctx.currentTime;
      const vol = 0.06 * volumeRef.current;
      playNote(523, now, 0.15, vol);        // C5
      playNote(659, now + 0.1, 0.15, vol);  // E5
      playNote(784, now + 0.2, 0.15, vol);  // G5
      playNote(1047, now + 0.3, 0.3, vol);  // C6
    } catch { /* */ }
  }, []);

  const playDefeat = useCallback(() => {
    if (!isEnabled()) return;
    playTone(400, 0.3, 'sawtooth', 0.05 * volumeRef.current, 180);
  }, []);

  const setVolume = useCallback((v: number) => { volumeRef.current = Math.max(0, Math.min(1, v)); }, []);
  const setEnabled = useCallback((e: boolean) => { enabledRef.current = e; }, []);

  return {
    playMove,
    playMerge,
    playSpawn,
    playTick,
    playCountdown,
    playGo,
    playVictory,
    playDefeat,
    setVolume,
    setEnabled,
    isEnabled: enabledRef.current,
  };
};

export default useSound;
